import React, {
  CSSProperties,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { cn } from "utils";
import { OnboardingCarouselProps } from "./types";

const TRANSITION_UNLOCK_MS = 450;
const CLONE_BOUNDARY_STUCK_MS = 480;

const toOffsetValue = (
  value: number | string | undefined,
  fallback: string,
): string => {
  if (value === undefined) {
    return fallback;
  }

  return typeof value === "number" ? `${value}px` : value;
};

export const OnboardingCarousel: React.FC<OnboardingCarouselProps> = ({
  items,
  autoplay = false,
  interval = 3000,
  className,
  indicatorPosition = "bottom",
  indicatorOffset,
  showIndicator = true,
  initialIndex = 0,
  onChange,
  delayRender = 0,
  indicatorClassName,
}) => {
  const hasLoop = items.length > 1;
  const cloneBannerContent = useCallback(
    (content: React.ReactNode, keySuffix: string) => {
      if (!isValidElement(content)) {
        return content;
      }

      return cloneElement(content, {
        key: `${content.key ?? "banner-content"}-${keySuffix}`,
      });
    },
    [],
  );

  const loopedItems = useMemo(() => {
    if (!hasLoop) {
      return items;
    }

    const firstItem = items[0];
    const lastItem = items[items.length - 1];

    return [
      {
        ...lastItem,
        id: `${lastItem.id}-clone-start`,
        content: cloneBannerContent(lastItem.content, "clone-start"),
      },
      ...items,
      {
        ...firstItem,
        id: `${firstItem.id}-clone-end`,
        content: cloneBannerContent(firstItem.content, "clone-end"),
      },
    ];
  }, [cloneBannerContent, hasLoop, items]);
  const itemsIdentity = useMemo(
    () => items.map((item) => String(item.id)).join("|"),
    [items],
  );
  const resolvedInitialIndex = hasLoop ? initialIndex + 1 : initialIndex;
  const [currentIndex, setCurrentIndex] = useState(resolvedInitialIndex);
  const currentIndexRef = useRef(resolvedInitialIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [isReady, setIsReady] = useState(delayRender === 0);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const currentXRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const stuckBoundaryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number | null>(null);
  const resetRafRef = useRef<number | null>(null);
  const isTransitioningRef = useRef(false);
  const isResettingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const lastTouchTimestampRef = useRef(0);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  const setSliderTransition = useCallback((enabled: boolean) => {
    if (!sliderRef.current) {
      return;
    }

    sliderRef.current.style.transition = enabled
      ? "transform 300ms ease-out"
      : "none";
  }, []);

  const getRealIndex = useCallback(
    (displayIndex: number) => {
      if (!hasLoop) {
        return Math.max(0, Math.min(displayIndex, items.length - 1));
      }

      if (displayIndex === 0) {
        return items.length - 1;
      }

      if (displayIndex === loopedItems.length - 1) {
        return 0;
      }

      return displayIndex - 1;
    },
    [hasLoop, items.length, loopedItems.length],
  );

  const activeIndex = getRealIndex(currentIndex);

  // 延迟渲染
  useEffect(() => {
    if (delayRender > 0 && !isReady) {
      const timer = setTimeout(() => {
        setIsReady(true);
      }, delayRender);
      return () => clearTimeout(timer);
    }
  }, [delayRender, isReady]);

  // 清除定时器
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const clearTransitionTimeout = useCallback(() => {
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);

  const clearStuckBoundaryTimeout = useCallback(() => {
    if (stuckBoundaryTimeoutRef.current) {
      clearTimeout(stuckBoundaryTimeoutRef.current);
      stuckBoundaryTimeoutRef.current = null;
    }
  }, []);

  const unlockTransition = useCallback(() => {
    clearTransitionTimeout();
    isTransitioningRef.current = false;
  }, [clearTransitionTimeout]);

  const clearResetRaf = useCallback(() => {
    if (resetRafRef.current) {
      cancelAnimationFrame(resetRafRef.current);
      resetRafRef.current = null;
    }
  }, []);

  const measureContainerWidth = useCallback(() => {
    if (!containerRef.current) {
      return containerWidthRef.current;
    }

    const nextWidth = containerRef.current.offsetWidth;
    if (nextWidth > 0) {
      containerWidthRef.current = nextWidth;
    }

    return containerWidthRef.current;
  }, []);

  const clampDisplayIndex = useCallback(
    (index: number) => {
      if (!hasLoop) {
        return Math.max(0, Math.min(index, items.length - 1));
      }

      return Math.max(0, Math.min(index, loopedItems.length - 1));
    },
    [hasLoop, items.length, loopedItems.length],
  );

  const isCloneBoundaryIndex = useCallback(
    (index: number) => {
      if (!hasLoop) {
        return false;
      }

      return index === 0 || index === loopedItems.length - 1;
    },
    [hasLoop, loopedItems.length],
  );

  // 更新slider位置
  const updateSliderPosition = useCallback(
    (index: number, dragOffset = currentXRef.current) => {
      if (!sliderRef.current) {
        return;
      }

      const containerWidth = measureContainerWidth();
      if (containerWidth <= 0) {
        return;
      }

      const safeIndex = clampDisplayIndex(index);
      const offset = -safeIndex * containerWidth + dragOffset;

      sliderRef.current.style.transform = `translate3d(${offset}px, 0, 0)`;
    },
    [clampDisplayIndex, measureContainerWidth],
  );

  const notifyIndexChange = useCallback(
    (displayIndex: number) => {
      onChange?.(getRealIndex(displayIndex));
    },
    [getRealIndex, onChange],
  );

  const resetToIndex = useCallback(
    (nextIndex: number) => {
      isResettingRef.current = true;
      clearResetRaf();
      clearStuckBoundaryTimeout();
      currentXRef.current = 0;
      currentIndexRef.current = nextIndex;
      setSliderTransition(false);
      // 边界重置时立即同步到真实位置，避免跨两帧出现可见横向瞬移
      updateSliderPosition(nextIndex, 0);
      setCurrentIndex(nextIndex);

      resetRafRef.current = requestAnimationFrame(() => {
        resetRafRef.current = requestAnimationFrame(() => {
          setSliderTransition(true);
          isResettingRef.current = false;
          unlockTransition();
          resetRafRef.current = null;
        });
      });
    },
    [
      clearResetRaf,
      clearStuckBoundaryTimeout,
      setSliderTransition,
      unlockTransition,
      updateSliderPosition,
    ],
  );

  const resetCloneBoundaryIfNeeded = useCallback(
    (index: number) => {
      if (!hasLoop) {
        return false;
      }

      if (index === 0) {
        resetToIndex(loopedItems.length - 2);
        return true;
      }

      if (index === loopedItems.length - 1) {
        resetToIndex(1);
        return true;
      }

      return false;
    },
    [hasLoop, loopedItems.length, resetToIndex],
  );

  const resolveLoopBoundaryIfNeeded = useCallback(() => {
    if (!hasLoop || isResettingRef.current) {
      return false;
    }

    const displayIndex = currentIndexRef.current;

    if (displayIndex <= 0) {
      resetToIndex(loopedItems.length - 2);
      return true;
    }

    if (displayIndex >= loopedItems.length - 1) {
      resetToIndex(1);
      return true;
    }

    return false;
  }, [hasLoop, loopedItems.length, resetToIndex]);

  const handleTransitionUnlockWithBoundaryCheck = useCallback(() => {
    if (resolveLoopBoundaryIfNeeded()) {
      return;
    }

    unlockTransition();
  }, [resolveLoopBoundaryIfNeeded, unlockTransition]);

  const scheduleStuckBoundaryRecovery = useCallback(
    (index: number) => {
      clearStuckBoundaryTimeout();

      if (
        !hasLoop ||
        isDraggingRef.current ||
        (!isCloneBoundaryIndex(index) &&
          index > 0 &&
          index < loopedItems.length - 1)
      ) {
        return;
      }

      stuckBoundaryTimeoutRef.current = setTimeout(() => {
        resolveLoopBoundaryIfNeeded();
      }, CLONE_BOUNDARY_STUCK_MS);
    },
    [
      clearStuckBoundaryTimeout,
      hasLoop,
      isCloneBoundaryIndex,
      loopedItems.length,
      resolveLoopBoundaryIfNeeded,
    ],
  );

  const canStartTransition = useCallback((nextIndex: number) => {
    if (isTransitioningRef.current) {
      return false;
    }

    if (nextIndex === currentIndexRef.current) {
      return false;
    }

    return true;
  }, []);

  const startAnimatedTransition = useCallback(
    (nextIndex: number) => {
      if (isResettingRef.current) {
        return false;
      }

      if (hasLoop && (nextIndex < 0 || nextIndex >= loopedItems.length)) {
        return resolveLoopBoundaryIfNeeded();
      }

      if (!canStartTransition(nextIndex)) {
        return false;
      }

      currentXRef.current = 0;
      isTransitioningRef.current = true;
      clearTransitionTimeout();
      clearStuckBoundaryTimeout();
      transitionTimeoutRef.current = setTimeout(() => {
        handleTransitionUnlockWithBoundaryCheck();
      }, TRANSITION_UNLOCK_MS);
      setSliderTransition(true);
      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
      notifyIndexChange(nextIndex);
      scheduleStuckBoundaryRecovery(nextIndex);

      return true;
    },
    [
      canStartTransition,
      clearStuckBoundaryTimeout,
      clearTransitionTimeout,
      handleTransitionUnlockWithBoundaryCheck,
      hasLoop,
      loopedItems.length,
      notifyIndexChange,
      resolveLoopBoundaryIfNeeded,
      scheduleStuckBoundaryRecovery,
      setSliderTransition,
    ],
  );

  // 切换到指定索引
  const goToIndex = useCallback(
    (index: number) => {
      const newIndex = hasLoop
        ? Math.max(1, Math.min(index + 1, loopedItems.length - 2))
        : Math.max(0, Math.min(index, items.length - 1));

      return startAnimatedTransition(newIndex);
    },
    [hasLoop, items.length, loopedItems.length, startAnimatedTransition],
  );

  // 下一张
  const goToNext = useCallback(() => {
    if (items.length <= 1) {
      return false;
    }

    if (!hasLoop) {
      const newIndex = Math.min(currentIndexRef.current + 1, items.length - 1);
      return startAnimatedTransition(newIndex);
    }

    const idx = currentIndexRef.current;

    if (idx >= loopedItems.length - 1) {
      return resolveLoopBoundaryIfNeeded();
    }

    return startAnimatedTransition(idx + 1);
  }, [
    hasLoop,
    items.length,
    loopedItems.length,
    resolveLoopBoundaryIfNeeded,
    startAnimatedTransition,
  ]);

  // 上一张
  const goToPrev = useCallback(() => {
    if (items.length <= 1) {
      return false;
    }

    if (!hasLoop) {
      const newIndex = Math.max(currentIndexRef.current - 1, 0);
      return startAnimatedTransition(newIndex);
    }

    const idx = currentIndexRef.current;

    if (idx <= 0) {
      return resolveLoopBoundaryIfNeeded();
    }

    return startAnimatedTransition(idx - 1);
  }, [
    hasLoop,
    items.length,
    loopedItems.length,
    resolveLoopBoundaryIfNeeded,
    startAnimatedTransition,
  ]);

  // 更新slider位置当索引改变时
  useEffect(() => {
    currentIndexRef.current = currentIndex;

    if (isResettingRef.current) {
      return;
    }

    updateSliderPosition(currentIndex);
  }, [currentIndex, updateSliderPosition]);

  useLayoutEffect(() => {
    // delayRender 场景：isReady=false 时 sliderRef/containerRef 还没挂载，等真实 DOM 渲染后再执行
    if (!isReady) {
      return;
    }

    currentXRef.current = 0;
    isTransitioningRef.current = false;
    isResettingRef.current = false;
    clearResetRaf();
    clearTransitionTimeout();
    clearStuckBoundaryTimeout();
    setSliderTransition(false);
    const loopInitialIndex = hasLoop ? initialIndex + 1 : initialIndex;
    currentIndexRef.current = loopInitialIndex;
    setCurrentIndex(loopInitialIndex);
    measureContainerWidth();
    updateSliderPosition(loopInitialIndex, 0);

    const frame = requestAnimationFrame(() => {
      setSliderTransition(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [
    clearResetRaf,
    clearStuckBoundaryTimeout,
    clearTransitionTimeout,
    hasLoop,
    isReady,
    items.length,
    initialIndex,
    itemsIdentity,
    measureContainerWidth,
    setSliderTransition,
    updateSliderPosition,
  ]);

  // 自动轮播
  useEffect(() => {
    if (!autoplay || items.length <= 1 || isDragging) {
      return;
    }

    clearTimer();
    timerRef.current = setTimeout(() => {
      if (!goToNext()) {
        clearTimer();
        timerRef.current = setTimeout(() => {
          goToNext();
        }, 300);
      }
    }, interval);

    return () => clearTimer();
  }, [
    autoplay,
    interval,
    items.length,
    currentIndex,
    goToNext,
    clearTimer,
    isDragging,
  ]);

  useEffect(() => {
    setSliderTransition(true);
  }, [setSliderTransition]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const handleContainerResize = () => {
      currentXRef.current = 0;
      measureContainerWidth();
      setSliderTransition(false);
      updateSliderPosition(currentIndexRef.current, 0);

      requestAnimationFrame(() => {
        setSliderTransition(true);
      });
    };

    const resizeObserver = new ResizeObserver(handleContainerResize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    isReady,
    measureContainerWidth,
    setSliderTransition,
    updateSliderPosition,
  ]);

  // 清理RAF
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      clearResetRaf();
      clearTransitionTimeout();
      clearStuckBoundaryTimeout();
    };
  }, [clearResetRaf, clearStuckBoundaryTimeout, clearTransitionTimeout]);

  // 触摸/鼠标开始
  const handleDragStart = useCallback(
    (clientX: number) => {
      if (isDraggingRef.current) {
        return;
      }

      isDraggingRef.current = true;
      setIsDragging(true);
      startXRef.current = clientX;
      currentXRef.current = 0;
      clearTimer();
      clearStuckBoundaryTimeout();

      // 取消过渡效果
      setSliderTransition(false);
    },
    [clearStuckBoundaryTimeout, clearTimer, setSliderTransition],
  );

  // 触摸/鼠标移动
  const handleDragMove = useCallback(
    (clientX: number) => {
      if (!isDraggingRef.current) {
        return;
      }

      const diff = clientX - startXRef.current;
      currentXRef.current = diff;

      // 使用RAF优化性能
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        updateSliderPosition(currentIndexRef.current, diff);
      });
    },
    [updateSliderPosition],
  );

  // 触摸/鼠标结束
  const handleDragEnd = useCallback(() => {
    if (!isDraggingRef.current) {
      return;
    }

    isDraggingRef.current = false;
    setIsDragging(false);

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const threshold = containerRef.current
      ? containerRef.current.offsetWidth * 0.2
      : 100;
    const offset = currentXRef.current;
    const idx = currentIndexRef.current;

    setSliderTransition(true);

    if (offset > threshold) {
      if (!goToPrev()) {
        currentXRef.current = 0;
        updateSliderPosition(idx, 0);
      }
    } else if (offset < -threshold) {
      if (!goToNext()) {
        currentXRef.current = 0;
        updateSliderPosition(idx, 0);
      }
    } else {
      currentXRef.current = 0;
      updateSliderPosition(idx, 0);
    }
  }, [goToPrev, goToNext, setSliderTransition, updateSliderPosition]);

  const handleTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (
        e.target !== e.currentTarget ||
        e.propertyName !== "transform" ||
        isDragging
      ) {
        return;
      }

      clearStuckBoundaryTimeout();

      if (!hasLoop) {
        unlockTransition();
        return;
      }

      const idx = currentIndexRef.current;

      if (resetCloneBoundaryIfNeeded(idx)) {
        return;
      }

      unlockTransition();
    },
    [
      clearStuckBoundaryTimeout,
      hasLoop,
      isDragging,
      resetCloneBoundaryIfNeeded,
      unlockTransition,
    ],
  );

  // 鼠标事件处理
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (Date.now() - lastTouchTimestampRef.current < 700) {
        return;
      }

      e.preventDefault();
      handleDragStart(e.clientX);
    },
    [handleDragStart],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        handleDragMove(e.clientX);
      }
    },
    [isDragging, handleDragMove],
  );

  const handleMouseUp = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleMouseLeave = useCallback(() => {
    if (isDragging) {
      handleDragEnd();
    }
  }, [isDragging, handleDragEnd]);

  // 触摸事件处理
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      lastTouchTimestampRef.current = Date.now();
      startYRef.current = e.touches[0].clientY;
      handleDragStart(e.touches[0].clientX);
    },
    [handleDragStart],
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (isDragging) {
        const deltaX = Math.abs(e.touches[0].clientX - startXRef.current);
        const deltaY = Math.abs(e.touches[0].clientY - startYRef.current);

        if (deltaX > deltaY) {
          e.preventDefault();
        }

        handleDragMove(e.touches[0].clientX);
      }
    },
    [isDragging, handleDragMove],
  );

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleTouchCancel = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const hasCustomHorizontalOffset =
    indicatorOffset?.left !== undefined || indicatorOffset?.right !== undefined;
  const indicatorPositionClass = hasCustomHorizontalOffset
    ? ""
    : {
        bottom: "left-1/2 -translate-x-1/2",
        "bottom-left": "left-0",
        "bottom-right": "right-0",
      }[indicatorPosition];
  const indicatorStyle = useMemo<CSSProperties>(() => {
    const style: CSSProperties = {
      bottom: `calc(env(safe-area-inset-bottom, 0px) + ${toOffsetValue(
        indicatorOffset?.bottom,
        "16px",
      )})`,
    };

    if (indicatorOffset?.left !== undefined) {
      style.left = toOffsetValue(indicatorOffset.left, "0px");
    }

    if (indicatorOffset?.right !== undefined) {
      style.right = toOffsetValue(indicatorOffset.right, "0px");
    }

    return style;
  }, [indicatorOffset?.bottom, indicatorOffset?.left, indicatorOffset?.right]);
  const placeholderItem = loopedItems[hasLoop ? 1 : 0] ?? loopedItems[0];

  if (items.length === 0) {
    return null;
  }

  // 延迟渲染占位符
  if (!isReady) {
    return (
      <div className={cn("relative w-full", className)}>
        <div className="relative w-full overflow-hidden">
          <div className="flex">
            <div className="flex-[0_0_100%] w-full flex-shrink-0 opacity-0">
              {placeholderItem?.content}
            </div>
          </div>
        </div>
        {showIndicator && items.length > 1 && (
          <div
            className={cn(
              "absolute z-10 flex gap-2 px-4 py-4",
              indicatorPositionClass,
              indicatorClassName,
            )}
            style={indicatorStyle}
          >
            {items.map((item) => (
              <div
                key={item.id}
                className="h-2 w-2 rounded-full bg-background_white_overlay10"
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={cn("relative w-full", className)}>
      {/* Banner内容容器 */}
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchCancel}
        style={{ touchAction: "pan-y" }}
      >
        <div
          ref={sliderRef}
          className="flex"
          onTransitionEnd={handleTransitionEnd}
          style={{
            willChange: "transform",
          }}
        >
          {loopedItems.map((item) => (
            <div key={item.id} className="flex-[0_0_100%] w-full flex-shrink-0">
              {item.content}
            </div>
          ))}
        </div>
      </div>

      {/* 点状指示器 */}
      {showIndicator && items.length > 1 && (
        <div
          className={cn(
            "absolute z-10 flex gap-2 px-4 py-4",
            indicatorPositionClass,
            indicatorClassName,
          )}
          style={indicatorStyle}
        >
          {items.map((item, index) => (
            <button
              data-openreplay-label="misc_onboardingcarousel_click"
              id="btn-onboarding-carousel"
              key={item.id}
              onClick={() => goToIndex(index)}
              className={cn(
                "h-2 rounded-full transition-all duration-300",
                index === activeIndex
                  ? "w-2 bg-background_brand_overlay100"
                  : "w-2 bg-background_white_overlay10 hover:bg-background_white_overlay20",
              )}
              aria-label={`jump to the ${index + 1}th item`}
              aria-current={index === activeIndex ? "true" : undefined}
            />
          ))}
        </div>
      )}
    </div>
  );
};
